import { motion } from "framer-motion";
import { Building2, Hammer, Zap, Droplets, PaintRoller, ArrowUpRight } from "lucide-react";
import { useLang } from "../lib/i18n";
import { IMG } from "../lib/dict";
import { SectionHead } from "./Reveal";
import { scrollToId } from "../lib/lenis";

const ICONS = { construction: Building2, renovation: Hammer, electrical: Zap, plumbing: Droplets, painting: PaintRoller };
const SPANS = {
  construction: "lg:col-span-7 lg:row-span-2 min-h-[420px]",
  renovation: "lg:col-span-5",
  electrical: "lg:col-span-5",
  plumbing: "lg:col-span-6",
  painting: "lg:col-span-6",
};

const Tile = ({ s, i }) => {
  const Icon = ICONS[s.key];
  return (
    <motion.article initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
      onClick={() => scrollToId("quote")} data-testid={`service-card-${s.key}`}
      className={`bento-tile group relative min-h-[300px] cursor-pointer overflow-hidden rounded-[22px] border border-emerald-500/20 bg-[#0C1210] ${SPANS[s.key]}`}>
      <img src={IMG[s.key]} alt={s.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover grayscale opacity-45 transition-[transform,opacity,filter] duration-[1200ms] group-hover:scale-105 group-hover:opacity-70 group-hover:grayscale-0" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050706] via-[#050706]/70 to-[#050706]/10" />
      <div className="relative flex h-full flex-col justify-between p-6 sm:p-8">
        <div className="flex items-start justify-between">
          <span className="grid h-12 w-12 place-items-center rounded-2xl border border-emerald-500/30 bg-[#050706]/60 text-[#00E676] backdrop-blur-md transition-colors duration-500 group-hover:bg-[#00E676] group-hover:text-black">
            <Icon className="h-5 w-5" />
          </span>
          <span className="font-mono text-xs text-zinc-300">0{i + 1}</span>
        </div>
        <div>
          <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">{s.title}</h3>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-300 sm:text-base">{s.text}</p>
          <span className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#00E676] opacity-80 transition-[gap,opacity] duration-300 group-hover:gap-3 group-hover:opacity-100">
            → <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </motion.article>
  );
};

export const Services = () => {
  const { t } = useLang();
  return (
    <section id="services" className="relative py-24 sm:py-36" data-testid="services-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <SectionHead eyebrow={t.services.eyebrow} title={t.services.title} accent={t.services.accent} testid="services-title" />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:auto-rows-[300px] lg:grid-cols-12 lg:gap-6">
          {t.services.items.map((s, i) => <Tile key={s.key} s={s} i={i} />)}
        </div>
      </div>
    </section>
  );
};
